import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { PublicHeader, PublicFooter } from "@/components/layouts/PublicLayout";
import AuthenticatedPageLayout from "@/components/layouts/AuthenticatedPageLayout";
import { useAuth } from "@/context/AuthContext";
import { ArrowRight } from "lucide-react";
import { serviceList } from "@/content/serviceDescriptions";
import useServiceModal from "@/hooks/useServiceModal";
import ServiceInfoModal from "@/components/modals/ServiceInfoModal";

const FUTURISTIC_IMAGE = "https://customer-assets.emergentagent.com/job_712ba195-f8b2-4b91-a610-23f4aedf7ae0/artifacts/2l0e6jbd_AI_Means_Business_Image_3.jpg";

const AboutPage = () => {
  const { user } = useAuth();
  const { activeServiceId, isOpen, openServiceModal, closeServiceModal } = useServiceModal();

  const content = (
    <>
      {/* Hero */}
      <section className="relative py-24 overflow-hidden">
        <div
          className="absolute inset-0 opacity-30"
          style={{
            backgroundImage: `url(${FUTURISTIC_IMAGE})`,
            backgroundSize: "cover",
            backgroundPosition: "center"
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-b from-navy-950 via-navy-950/90 to-navy-950" />
        
        <div className="relative max-w-4xl mx-auto px-6 text-center">
          <p className="text-primary font-medium mb-4 tracking-wide uppercase text-sm">
            About Us
          </p>
          <h1 className="font-heading text-5xl md:text-6xl font-extrabold text-white mb-6 tracking-tight">
            Human Guidance for an A.I. World
          </h1>
          <p className="text-lg md:text-xl text-slate-300 max-w-2xl mx-auto">
            We help established businesses navigate the complexities of A.I. adoption 
            with clarity, wisdom, and strategic foresight.
          </p>
        </div>
      </section>

      {/* Mission */}
      <section className="py-24 bg-navy-900">
        <div className="max-w-4xl mx-auto px-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-16">
            <div>
              <h2 className="font-heading text-3xl font-bold text-white mb-6 accent-underline">
                Our Mission
              </h2>
              <p className="text-slate-300 leading-relaxed mb-4">
                A.I. is transforming every industry at an unprecedented pace. For established 
                businesses, this presents both enormous opportunities and significant risks.
              </p>
              <p className="text-slate-300 leading-relaxed">
                Our mission is to be the trusted guide that helps business leaders make 
                informed A.I. decisions—reducing risk, maximizing opportunity, and ensuring 
                that human judgment remains at the center of strategic choices.
              </p>
            </div>
            <div>
              <h2 className="font-heading text-3xl font-bold text-white mb-6 accent-underline">
                Our Approach
              </h2>
              <p className="text-slate-300 leading-relaxed mb-4">
                We don't sell A.I. tools or implementations. We sell clarity. Our advisory 
                engagements are designed to give you the knowledge and confidence to make 
                A.I. decisions that align with your business goals.
              </p>
              <p className="text-slate-300 leading-relaxed">
                Every business is unique. We take the time to understand your specific 
                context, challenges, and opportunities before making any recommendations.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Values */}
      <section className="py-24 bg-navy-950">
        <div className="max-w-4xl mx-auto px-6">
          <h2 className="font-heading text-4xl font-bold text-white mb-12 text-center">
            Our Core Values
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              {
                title: "Clarity Over Complexity",
                description: "We cut through the AI hype to deliver straightforward, actionable guidance."
              },
              {
                title: "Human-Centered",
                description: "Technology should serve people, not the other way around. We keep humans at the center."
              },
              {
                title: "Strategic Integrity",
                description: "We only recommend what's right for your business, even if that means advising caution."
              }
            ].map((value, index) => (
              <div key={index} className="glass p-8 rounded-2xl" data-testid={`value-${index}`}>
                <h3 className="font-heading text-xl font-semibold text-white mb-3">{value.title}</h3>
                <p className="text-slate-400">{value.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How We Help Section */}
      <section className="py-24 bg-navy-900">
        <div className="max-w-4xl mx-auto px-6">
          <h2 className="font-heading text-4xl font-bold text-white mb-4 text-center">
            How We Help
          </h2>
          <p className="text-slate-400 text-center mb-12 max-w-2xl mx-auto">
            Our services are designed to provide clarity at every stage of your A.I. journey—without 
            pushing you toward solutions you don't need.
          </p>
          
          <div className="space-y-6">
            {serviceList.map((service) => (
              <div 
                key={service.id}
                className="border-b border-slate-800 pb-6 last:border-b-0"
                data-testid={`about-service-${service.id}`}
              >
                <h3 className="font-heading text-xl font-semibold text-white mb-2">
                  {service.title}
                </h3>
                <p className="text-slate-400 mb-3 leading-relaxed">
                  {service.shortSummary}
                </p>
                <button
                  onClick={() => openServiceModal(service.id)}
                  className="text-primary hover:text-primary/80 font-medium text-sm transition-colors"
                  data-testid={`about-service-link-${service.id}`}
                >
                  About this service →
                </button>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 bg-navy-900">
        <div className="max-w-3xl mx-auto px-6 text-center">
          <h2 className="font-heading text-4xl font-bold text-white mb-6">
            Ready to Start Your A.I. Journey?
          </h2>
          <p className="text-slate-300 mb-8">
            Let us help you navigate A.I. transformation with confidence and clarity.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/get-started" data-testid="about-cta">
              <Button className="bg-primary hover:bg-primary/90 text-white font-bold rounded-full px-8 py-4">
                Get Your Free A.I. Business Evaluation
                <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
            </Link>
            <Link to="/about-founder" data-testid="about-founder-link">
              <Button variant="outline" className="border-slate-700 text-slate-300 hover:text-white rounded-full px-8 py-4">
                Meet Our Founder
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Service Info Modal */}
      <ServiceInfoModal
        serviceId={activeServiceId}
        isOpen={isOpen}
        onClose={closeServiceModal}
      />
    </>
  );

  // Render with authenticated layout if user is logged in
  if (user) {
    return <AuthenticatedPageLayout>{content}</AuthenticatedPageLayout>;
  }

  // Render with public layout for anonymous users
  return (
    <div className="min-h-screen bg-background">
      <PublicHeader />
      {content}
      <PublicFooter />
    </div>
  );
};

export default AboutPage;
